const EXPIRATION_TIME_MS = 1000 * 60 * 60 * 24;
const LONG_EXPIRATION_TIME_MS = 1000 * 60 * 60 * 24 * 365;

export default class StorageUtil {

    /**
     * Load state by key
     * @param key
     * @returns {object or undefined}
    */
    static loadState(key) {
        try {
            const serializedState = this._getStorage(key).data;
            if(serializedState === null) {
                return undefined;
            }
            return JSON.parse(serializedState);
        } catch(e) {
            return undefined;
        }
    }

    /**
     * Set state by key
     * @param state
     * @param key
     */
    static saveState(state, key) {
        try {
            const serializedState = JSON.stringify(state);
            this._setStorage(key, {
                data: serializedState,
                userId: this.getLoggedInUserId(),
                writeTime: Date.now()
            });
        } catch(e) {
            //do nothing
        }
    }

    /**
     * Get feature flag
     * @param key
     * @returns {boolean}
     */
    static getFeatureFlag(key) {
        try {
            const featureFlag = window.localStorage[key];

            if (typeof featureFlag === 'undefined' || featureFlag === null) {
                return false;
            } else if (typeof featureFlag === 'string') {
                switch (featureFlag.toLowerCase()) {
                case 'false':
                case '':
                    return false;
                default:
                    return true;
                }
            } else if (typeof featureFlag === 'number') {
                return featureFlag !== 0;
            }
            else {return true;}
        } catch(e) {
            return false;
        }
    }

    static setFeatureFlag(key, value) {
        this._setLocalStorage(key, value);
    }

    /**
     * Get storage by key
     * @param key
     * @returns {*}
     * @private
     */
    static _getStorage(key) {
        const storageKey =  key;
        let local = this._getLocalStorage(storageKey);
        let session = this._getSessionStorage(storageKey);
        const userId = this.getLoggedInUserId();

        // Ignore local storage if it was made under a different user ID
        if (userId && local && local.userId !== userId) {
            local = null;
        }

        local = this._enforceExpirationTime(local);
        session = this._enforceExpirationTime(session);

        if (!userId) {
            // Don't use local storage if user is not logged in.
            session = session || {};
            return session;
        }

        return local || session || {};
    }

    /**
     * Set local storage by key
     * @param key
     * @param data
     * @private
     */
    static _setLocalStorage(key, data) {
        if (!data) {
            delete window.localStorage[key];
        } else {
            window.localStorage[key] = JSON.stringify(data);
        }
    }

    /**
     * Set session storage by key
     * @param key
     * @param data
     * @private
     */
    static _setSessionStorage(key, data) {
        if (!data) {
            delete window.sessionStorage[key];
        } else {
            window.sessionStorage[key] = JSON.stringify(data);
        }
    }

    /**
     * Get local storage by key
     * @param key
     * @returns {*}
     * @private
     */
    static _getLocalStorage(key) {
        return this._parseJson(window.localStorage[key]);
    }

    /**
     * Get session storage by key
     * @param key
     * @returns {*}
     * @private
     */
    static _getSessionStorage(key) {
        return this._parseJson(window.sessionStorage[key]);
    }

    /**
     * Return loggedIn UserId
     * @returns {*}
     */
    static getLoggedInUserId() {
        if (window.Shr && window.Shr.U && window.Shr.U.oauthToken && window.Shr.U.oflyTicket) {
            return window.Shr.U.userId;
        }

        return null;
    }

    /**
     * Set storage
     * @param key
     * @param data
     * @private
     */
    static _setStorage(key, data) {
        const storageKey = key;
        if (this.getLoggedInUserId()) {
            this._setLocalStorage(storageKey, data);
            this._setSessionStorage(storageKey, null);
        } else {
            this._setSessionStorage(storageKey, data);
        }
    }

    /**
     * Set cookie by name
     * @param cname
     * @param cvalue
     */
    static setCookie(cname, cvalue) {
        var d = new Date();
        d.setTime(d.getTime() + (LONG_EXPIRATION_TIME_MS));
        var expires = `expires=${d.toUTCString()}`;
        document.cookie = `${cname}=${cvalue};${expires};path=/`;
    }

    /**
     * Get cookie by name
     * @param name
     * @returns {*}
     */
    static getCookie(name) {
        const nameEQ = `${name}=`;
        const ca = document.cookie.split(';');
        for(let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') {
                c = c.substring(1, c.length);
            }
            if (c.indexOf(nameEQ) === 0) {
                return c.substring(nameEQ.length, c.length);
            }
        }
        return null;
    }

    /**
     * Return empty object when storage is expired
     * @param storage
     * @returns {*}
     * @private
     */
    static _enforceExpirationTime(storage) {
        if (storage && !storage.writeTime) {
            return {};
        }
        else if (storage && storage.writeTime && (Date.now() - storage.writeTime) > EXPIRATION_TIME_MS) {
            return {};
        } else {
            return storage;
        }
    }

    /**
     * Parse JSON string
     * @param json
     * @returns {object}
     * @private
     */
    static _parseJson(json) {
        if (!json) {
            return null;
        }

        try {
            return JSON.parse(json);
        } catch (e) {
            return null;
        }
    }
}
